import { Node } from 'slate';

import { ALINE_DRAFT_DATA_ATTRIBUTES } from '../../agent-harness';
import type {
  ListDefinitions,
  ParagraphElement,
  TableCellElement,
  TableRowElement,
} from '../../slate-api';
import { isParagraphElement, isTableElement, Queries } from '../../slate-api';

/**
 * Serializes removed blocks (paragraphs and tables) to the minimal HTML the
 * chat card preview renders struck-through. Structure only — tables keep
 * their rows and cells, list items keep their `data-aline-list-*` attributes
 * (the drafter encoding the client preview turns into markers and
 * indentation), and everything else flattens to its plain text — because the
 * preview's job is showing WHAT is being removed, not reproducing its
 * formatting. Unlike `alineToHtmlForClipboard` this is string-built, so it
 * runs server-side.
 *
 * The preview is intentionally uncapped, matching the insert card: capping
 * would force either broken-markup truncation or a worse plain-text fallback,
 * and the structure-only output is roughly proportional to the removed text.
 */
export function serializeRemovedBlocksToPreviewHtml(
  blocks: Node[],
  options: { listDefinitions: ListDefinitions }
): string {
  return blocks
    .map(block => {
      if (isTableElement(block)) return serializeRemovedRowsToPreviewHtml(block.children);
      const listAttributes = isParagraphElement(block)
        ? serializeListItemAttributes(block, options.listDefinitions)
        : '';
      return `<p${listAttributes}>${escapeHtml(Node.string(block))}</p>`;
    })
    .join('');
}

/**
 * The `data-aline-list-*` attributes for a removed list-item paragraph — the
 * same encoding the drafter emits, so the client preview draws the marker and
 * indentation. Non-list paragraphs contribute no attributes. The style
 * attribute carries only the level's marker-style name; when the definition
 * is missing (or the style is custom) the client falls back to a standard
 * marker.
 */
function serializeListItemAttributes(
  paragraph: ParagraphElement,
  listDefinitions: ListDefinitions
): string {
  const listProps = Queries.renderedParagraphListProps(paragraph);
  if (!listProps) return '';

  const attributes = [
    `${ALINE_DRAFT_DATA_ATTRIBUTES.listId}="${escapeHtml(listProps.listId)}"`,
    `${ALINE_DRAFT_DATA_ATTRIBUTES.listLevel}="${listProps.listLevel}"`,
  ];
  const styleName = listDefinitions[listProps.listId]?.[listProps.listLevel]?.style.name;
  if (styleName) {
    attributes.push(`${ALINE_DRAFT_DATA_ATTRIBUTES.listStyle}="${escapeHtml(styleName)}"`);
  }
  return ` ${attributes.join(' ')}`;
}

/**
 * Serializes table rows to a preview `<table>` — the removed-rows and
 * removed-table cards' preview shape.
 */
export function serializeRemovedRowsToPreviewHtml(rows: TableRowElement[]): string {
  return serializeCellGridToPreviewHtml(rows.map(row => row.children));
}

/**
 * Serializes the cells a column removal strikes — one array of cells per
 * table row — to the same preview `<table>` shape the removed-rows card uses.
 */
export function serializeRemovedColumnCellsToPreviewHtml(cellsByRow: TableCellElement[][]): string {
  return serializeCellGridToPreviewHtml(cellsByRow);
}

/**
 * Serializes plain-text lines (one per removed PDF paragraph) to the same
 * `<p>`-per-block preview HTML the collab-editor path emits.
 */
export function serializeRemovedTextLinesToPreviewHtml(lines: string[]): string {
  return lines.map(line => `<p>${escapeHtml(line)}</p>`).join('');
}

/**
 * How much cell text a precomputed PDF table preview may hold before
 * truncating. Unlike the card-time previews above — which are intentionally
 * uncapped because they're built only when a suggestion exists — PDF table
 * previews are built eagerly for every table of every PDF an AI reads and
 * mostly never shown, so an entire-document-in-a-table PDF must not eat the
 * memory.
 */
const PDF_TABLE_PREVIEW_CHAR_CAP = 3000;

/** How many rows a PDF table preview always includes, cap notwithstanding. */
const PDF_TABLE_PREVIEW_MIN_ROWS = 2;

/**
 * Serializes a PDF table's cell texts (rows × cells; multi-paragraph cells
 * join their text with `\n`) to the same preview `<table>` shape the DOCX
 * removed-rows card uses. Small tables preview in full; past the character
 * cap the table ends with an indicator row admitting the truncation.
 */
export function serializePdfTableToPreviewHtml(cellTextsByRow: string[][]): string {
  const rowsHtml: string[] = [];
  let totalChars = 0;
  for (const cells of cellTextsByRow) {
    if (rowsHtml.length >= PDF_TABLE_PREVIEW_MIN_ROWS && totalChars >= PDF_TABLE_PREVIEW_CHAR_CAP) {
      const omittedCount = cellTextsByRow.length - rowsHtml.length;
      rowsHtml.push(`<tr><td>… +${omittedCount} more rows</td></tr>`);
      break;
    }
    const cellsHtml = cells
      .map(cellText => `<td>${escapeHtml(cellText).replaceAll('\n', '<br>')}</td>`)
      .join('');
    rowsHtml.push(`<tr>${cellsHtml}</tr>`);
    totalChars += cells.reduce((sum, cellText) => sum + cellText.length, 0);
  }
  return `<table>${rowsHtml.join('')}</table>`;
}

function serializeCellGridToPreviewHtml(cellsByRow: TableCellElement[][]): string {
  const rowsHtml = cellsByRow
    .map(cells => {
      const cellsHtml = cells
        .map(cell => {
          const cellText = cell.children
            .map(paragraph => escapeHtml(Node.string(paragraph)))
            .join('<br>');
          return `<td>${cellText}</td>`;
        })
        .join('');
      return `<tr>${cellsHtml}</tr>`;
    })
    .join('');
  return `<table>${rowsHtml}</table>`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
